'use client';

import clsx from 'clsx';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Input } from '@/components/ui/Input';
import { Select, type SelectOption } from '@/components/ui/Select';
import { usePhoneInput } from '@/hooks/usePhoneInput';
import { getPhonePrefixCodeSelectOptions } from '@/lib/phonePrefixes/helpers';

import styles from './PhoneInput.module.scss';

export interface PhoneInputProps {
  prefixFieldName?: string;
  phoneFieldName?: string;
  prefixLabel?: string;
  phoneLabel?: string;
  phonePlaceholder?: string;
  lang?: string;
  country?: string;
  disabled?: boolean;
  required?: boolean;
  /** External error message for the phone field (takes precedence over form-context errors) */
  error?: string;
  /** Color variant: primary (green) for light backgrounds, secondary (white) for dark backgrounds */
  variant?: 'primary' | 'secondary';
  className?: string;
}

export function PhoneInput({
  prefixFieldName = 'phonePrefix',
  phoneFieldName = 'phone',
  prefixLabel = 'Prefix',
  phoneLabel = 'Telephone number',
  phonePlaceholder,
  lang,
  country,
  disabled = false,
  required = false,
  error: externalError,
  variant = 'primary',
  className,
}: PhoneInputProps) {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();

  const { examplePhone } = usePhoneInput({ prefixFieldName, phoneFieldName });

  const phonePrefixOptions: SelectOption[] = useMemo(() => getPhonePrefixCodeSelectOptions(), []);

  const prefixError = errors[prefixFieldName]?.message as string | undefined;
  const phoneError = externalError || (errors[phoneFieldName]?.message as string | undefined);

  return (
    <div className={clsx(styles.phoneRow, className)}>
      <div className={styles.prefixField}>
        <Controller
          name={prefixFieldName}
          control={control}
          render={({ field }) => (
            <Select
              label={prefixLabel}
              options={phonePrefixOptions}
              value={field.value}
              onChange={field.onChange}
              disabled={disabled}
              required={required}
              error={prefixError}
              variant={variant}
              fullWidth
            />
          )}
        />
      </div>

      <div className={styles.phoneField}>
        <Input
          type="tel"
          {...register(phoneFieldName)}
          label={phoneLabel}
          disabled={disabled}
          required={required}
          autoComplete="tel-national"
          inputMode="tel"
          placeholder={phonePlaceholder ?? examplePhone}
          pattern="[0-9+\(\)\-\s]*"
          error={phoneError}
          variant={variant}
        />
      </div>
    </div>
  );
}
